import React, { createContext, useCallback, useContext, ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import { auth, Unsubscribe } from 'firebase';
import axios from 'axios';
import api from '../../services/api';
import { StickerFullProps } from '../../assets/stickers/full_sticker';
import { appEnv } from '../../appEnv';

export type Whitelabel = {
  alias: string;
  name: string;
  fullName?: string;
  hideDisconnectedStations?: boolean;
  favicon: string;
  linkBranchIo?: string;
  qrCodeLink: string;
  full_sticker?: Omit<StickerFullProps, 'qrcode' | 'station'>;
  qrCode?: {
    logo: string;
    size_logo: {
      width: number;
      height: number;
    };
    url_prefix: string;
  };
};

export type IconPack = {
  alias: string;
  name: string;
};

type EmspWhiteLabel = { slug: string; app_alias: string };

interface AppThemeContextType {
  selectedWhitelabelTheme?: Whitelabel;
  allWhitelabels: Record<string, Whitelabel>;
  availableIconPacks: IconPack[];
  emspWhiteLabels: EmspWhiteLabel[];
  isEmspConfigured: boolean;
  resetWhitelabelThemeToDefault: () => void;
  getWhitelabelByAlias: (alias: string, fallbackToBrand?: boolean) => Whitelabel | null;
  setSelectedWhitelabelByAlias: (alias: string) => void;
  getEmspStationLink: (alias: string | undefined, stationId: string) => string | undefined;
}

export const BRAND_WHITELABEL_ALIAS = "tupinamba";
const QRCODE_URL_PREFIX = appEnv.REACT_APP_QRCODE_API_URL || '';
const EMSP_WEB_URL = appEnv.REACT_APP_EMSP_WEB_URL || '';

const fetchEmspWhiteLabels = async (): Promise<EmspWhiteLabel[]> => {
  if (!EMSP_WEB_URL) return [];
  try {
    const cleanUrl = EMSP_WEB_URL.replace(/\/$/, '');
    const { data } = await axios.get<EmspWhiteLabel[]>(`${cleanUrl}/white-labels`);
    return data;
  } catch {
    return [];
  }
};

const WhitelabelThemesContext = createContext<AppThemeContextType | undefined>(undefined);

export const WhitelabelThemesProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const authStateChangedUnsubscribe = useRef<Unsubscribe>();
  const defaultWhitelabel = useRef<string | undefined>();
  const [selectedWhitelabelTheme, setSelectedWhitelabelTheme] = useState<Whitelabel | undefined>();
  const [allWhitelabels, setAllWhitelabels] = useState<Record<string, Whitelabel>>({});
  const [availableIconPacks, setavailableIconPacks] = useState<IconPack[]>([]);
  const [emspWhiteLabels, setEmspWhiteLabels] = useState<EmspWhiteLabel[]>([]);

  const fetchIconPacks = async () => {
    try {
      const { data: iconPacks } = await api.get("stations/icon-packs");
      setavailableIconPacks(iconPacks);
    } catch (error) {
      console.error("Error fetching icon packs", error);
    }
  };

  const fetchAllWhitelabels = async (): Promise<{allApps: Record<string, Whitelabel>, defaultAppAlias: string}> => {
    const [{ data }, emspWls] = await Promise.all([
      api.get("white-label?includeTupi=true"),
      fetchEmspWhiteLabels(),
    ]);
    setEmspWhiteLabels(emspWls);
    const { allApps: allAppsList, defaultAppAlias } = data;
    const allApps = allAppsList.reduce((acc: Record<string, Whitelabel>, app: any) => {
      const qrCodeTarget = app.qrCodeTarget ?? app.alias;
      acc[app.alias] = {
        alias: app.alias,
        name: app.name,
        fullName: app.fullName,
        qrCodeLink: app.qrCodeLink,
        hideDisconnectedStations: app.hideDisconnectedStations,
        favicon: app.favicon,
        linkBranchIo: app.deepLinkURL,
        full_sticker: app.fullSticker,
        qrCode: app.qrCode ? {
          ...app.qrCode,
          logo: app.favicon,
          url_prefix: qrCodeTarget ? `${QRCODE_URL_PREFIX}/${qrCodeTarget}` : QRCODE_URL_PREFIX,
        } : undefined
      };
      return acc;
    }, {} as Record<string, Whitelabel>);
    return {allApps, defaultAppAlias}
  }

  useEffect(() => {
    const subscribeToAuthStateChanged = () => {
      authStateChangedUnsubscribe.current = auth().onAuthStateChanged(async (user) => {
        if(!user) return;
        await fetchIconPacks();
        const {allApps, defaultAppAlias} = await fetchAllWhitelabels();
        setAllWhitelabels(allApps);
        defaultWhitelabel.current = defaultAppAlias;
        setSelectedWhitelabelTheme(allApps[defaultAppAlias]);
      });
    };

    subscribeToAuthStateChanged();
    return () => {
      if (authStateChangedUnsubscribe.current)
        authStateChangedUnsubscribe.current();
    };
  }, []);

  const getEmspStationLink = useCallback((alias: string | undefined, stationId: string): string | undefined => {
    if (!EMSP_WEB_URL || !stationId) return undefined;
    try {
      const url = new URL(EMSP_WEB_URL.replace(/\/$/, ''));
      if (alias && alias !== BRAND_WHITELABEL_ALIAS) {
        const emspWl = emspWhiteLabels.find(wl => wl.app_alias === alias);
        if (emspWl) {
          url.hostname = `${emspWl.slug}.${url.hostname}`;
        }
      }
      return `${url.origin}/stations/${stationId}`;
    } catch {
      return undefined;
    }
  }, [emspWhiteLabels]);

  const whitelabelThemesContextValue = useMemo(() => {
    const resetWhitelabelThemeToDefault = () => {
      if (defaultWhitelabel.current) {
        setSelectedWhitelabelTheme(allWhitelabels[defaultWhitelabel.current]);
      }
    }

    const getWhitelabelByAlias = (alias: string, fallbackToBrand: boolean = false): Whitelabel | null => {
      const whitelabel = allWhitelabels[alias] as Whitelabel | undefined;
      if (whitelabel) return whitelabel;
      if (fallbackToBrand) return allWhitelabels[BRAND_WHITELABEL_ALIAS];
      return null;
    }

    const setSelectedWhitelabelByAlias = (alias: string) => {
      setSelectedWhitelabelTheme(allWhitelabels[alias]);
    }

    return {
      selectedWhitelabelTheme,
      allWhitelabels,
      availableIconPacks,
      emspWhiteLabels,
      isEmspConfigured: !!EMSP_WEB_URL,
      resetWhitelabelThemeToDefault,
      getWhitelabelByAlias,
      setSelectedWhitelabelByAlias,
      getEmspStationLink,
    }
   }, [selectedWhitelabelTheme, allWhitelabels, availableIconPacks, emspWhiteLabels, getEmspStationLink]);

  return (
    <WhitelabelThemesContext.Provider value={whitelabelThemesContextValue}>
      {children}
    </WhitelabelThemesContext.Provider>
  );
};

export const useWhitelabels = () => {
  const context = useContext(WhitelabelThemesContext);
  if (context === undefined) {
    throw new Error('useWhitelabels must be used within a WhitelabelThemesProvider');
  }
  return context;
};
